import {
  AutoReplyTypeEnum,
  CannedMessageTypeEnum,
  ChannelStateTypeEnum,
  ChannelTypeEnum,
  ContactTypeEnum,
  DefaultChannelsEnum,
  MessageTypeEnum,
  SMSDeliveryStatusEnum,
} from '@kukuicorp/connect-chat-realm-models';
import { DateTime } from 'luxon';
import debounce from 'lodash/debounce';
import { makeStyles } from '@material-ui/core';

export {
  MessageTypeEnum,
  ContactTypeEnum,
  ChannelTypeEnum,
  SMSDeliveryStatusEnum,
  DefaultChannelsEnum,
  CannedMessageTypeEnum,
  ChannelStateTypeEnum,
  AutoReplyTypeEnum,
};

export const reconnectAttemptNumber = 60 * 60 * 24;

export const maxNumberOfItemsByContainerSize = (
  document,
  containerRef,
  divider = 34,
  customHeightToDeduct = 0
) => {
  let maxNumberOfItems = 20;

  const currentMax = containerRef?.current?.clientHeight
    ? Math.round(containerRef.current.clientHeight / divider)
    : Math.round((window.innerHeight - customHeightToDeduct) / divider);

  if (currentMax !== 0 && currentMax > maxNumberOfItems) {
    maxNumberOfItems = currentMax;
  }

  return maxNumberOfItems;
};

export const mode = {
  Connect: 'Connect',
  CpNotificationHub: 'CpNotificationHub',
};

export const availableModes = {
  CustomerChat: 'CustomerChat',
  WebsiteChat: 'WebsiteChat',
};

export function mapData(data) {
  return {
    settings: data.settings,
    channels: data.channels,
    loggedUser: data.settings.User,
    messages: data.messages ? data.messages : [],
    messageTypes: data.messageTypes ? data.messageTypes : [],
    channelTypes: data.channelTypes ? data.channelTypes : [],
    contactTypes: data.contactTypes ? data.contactTypes : [],
    smsDeliveryStatus: data.smsDeliveryStatus ? data.smsDeliveryStatus : [],
    lastSeenMessageForChannel: data.lastSeenMessageForChannel ? data.lastSeenMessageForChannel : [],
    autoReplies: data.autoReplies ? data.autoReplies : [],
    autoRepliesV2: data.autoRepliesV2 ? data.autoRepliesV2 : [],
    locationTrackingNumber: data.locationTrackingNumber,
    cannedMessages: data.cannedMessages || [],
    featureFlags: data.featureFlags || [],
    availableModes: data.availableModes || [],
    cannedMessageTypes: data.cannedMessageTypes ? data.cannedMessageTypes : [],
    isWebchatEnabled: data.isWebchatEnabled || false,
    isAutoCloseWebChatEnabled: data.isAutoCloseWebChatEnabled || false,
    autoReplyTypes: data.autoReplyTypes ? data.autoReplyTypes : [],
    workTime: data.workTime,
    clientWebsiteUrl: data.clientWebsiteUrl,
    customersData: data.customersData,
  };
}

export function generateGuid() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }

  return `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

export function formatPhoneNumber(number) {
  const s2 = `${number}`.replace(/\D/g, '');
  const m = s2.match(/^(\d{3})(\d{3})(\d{4})$/); // eslint-disable-line
  return !m ? s2 : `(${m[1]}) ${m[2]}-${m[3]}`;
}

export const getUniqueFromArray = (incArray, prop) => {
  const filteredArray = incArray.map((el) => el[prop]);
  return incArray.filter((obj, index) => filteredArray.indexOf(obj[prop]) === index);
};

export const windowName = 'Connect Chat';

export function prettifyMessageType(messageType) {
  switch (messageType) {
    case MessageTypeEnum.SMS.TypeName:
      return 'SMS';
    case MessageTypeEnum.System.TypeName:
      return 'Control Panel';
    case MessageTypeEnum.AutoReply.TypeName:
      return 'Auto Reply';
    default:
      return 'Direct Message';
  }
}

export const rightSideComponents = {
  CannedMessagesList: 'CannedMessagesList',
  ChannelInfo: 'ChannelInfo',
};

export const mainComponentOpen = {
  editCannedMessages: 'editCannedMessages',
};

export const getUrlParameter = (name, url = window.location.search) => {
  // eslint-disable-next-line no-useless-escape
  const cleanName = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp(`[\\?&]${cleanName}=([^&#]*)`);
  const results = regex.exec(url);

  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

export const inIframe = () => getUrlParameter('embed') === 'true';

export const getColorFromName = (userName) => {
  let sumChars = 0;
  if (userName) {
    for (let i = 0; i < userName.length; i += 1) {
      sumChars += userName.charCodeAt(i);
    }
  }

  const colors = [
    '#1abc9c',
    '#2ecc71',
    '#9b59b6',
    '#34495e',
    '#16a085',
    '#27ae60',
    '#2980b9',
    '#8e44ad',
    '#2c3e50',
    '#f1c40f',
    '#f39c12',
    '#e74c3c',
    '#f39c12',
    '#d35400',
    '#c0392b',
  ];

  return { backgroundColor: colors[sumChars % colors.length] };
};

export const setFaviconBadgeCountDebounced = debounce((favicon, count) => {
  if (favicon) {
    favicon.badge(count);
  }
}, 250);

export const getCannedMessageTypeForChannelType = (channelTypeId) => {
  if (channelTypeId === ChannelTypeEnum.WebChat.TypeId) return CannedMessageTypeEnum.WebChat;

  return CannedMessageTypeEnum.SMS;
};

export const getSettingsRadioButtonStyle = () =>
  makeStyles({
    radioButton: {
      verticalAlign: 'top',
      padding: 0,
      margin: 0,
      color: '#9A9B99',
    },
    checked: {
      color: '#00892e',
    },
  });

export const getTimeDifferenceInHoursMinutesSeconds = (firstDate, secondDate) => {
  const secNum = Math.floor(firstDate - secondDate) / 1000;
  const days = Math.floor(secNum / (3600 * 24));
  const hours = Math.floor((secNum - days * (3600 * 24)) / 3600);
  const minutes = Math.floor((secNum - days * (3600 * 24) - hours * 3600) / 60);
  const seconds = Math.floor(secNum - days * (3600 * 24) - hours * 3600 - minutes * 60);

  return `${days !== 0 ? `${days}:` : ''}${`${hours}`.padStart(2, '0')}:${`${minutes}`.padStart(
    2,
    '0'
  )}:${`${seconds}`.padStart(2, '0')}`;
};

export const daySorter = {
  sunday: 1,
  monday: 2,
  tuesday: 3,
  wednesday: 4,
  thursday: 5,
  friday: 6,
  saturday: 7,
};

export const getAvailableModesForLocation = (location) => {
  const availableModesForLocation = [];

  if (location.CustomerChatIsActive) {
    availableModesForLocation.push(availableModes.CustomerChat);
  }

  if (location.WebsiteChatIsActive) {
    availableModesForLocation.push(availableModes.WebsiteChat);
  }

  return availableModesForLocation;
};

export const isValidPhoneNumber = (searchInput) => {
  if (searchInput) {
    const rawInput = searchInput.replace(/[^\d]/g, '');

    return /^\d{10}$/.test(rawInput);
  }

  return false;
};

export const getDayFromShortName = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 0,
};

export function get24HrsFrmAMPM(timeStr) {
  if (timeStr && timeStr.indexOf(' ') !== -1 && timeStr.indexOf(':') !== -1) {
    let hrs = 0;
    const tempAry = timeStr.split(' ');
    const hrsMinAry = tempAry[0].split(':');
    hrs = parseInt(hrsMinAry[0], 10);
    if ((tempAry[1] === 'AM' || tempAry[1] === 'am') && hrs === 12) {
      hrs = 0;
    } else if ((tempAry[1] === 'PM' || tempAry[1] === 'pm') && hrs !== 12) {
      hrs += 12;
    }
    return `${`0${hrs}`.slice(-2)}:${`0${parseInt(hrsMinAry[1], 10)}`.slice(-2)}`;
  }

  return null;
}

const daysOfTheWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const parseRecurringStatus = (recurringStatus) => {
  const statuses = recurringStatus.split('|');

  return statuses.map((status) => {
    const [daysString, timesString] = status.split('; ');

    const days = daysString
      .split(', ')
      .sort((a, b) => daysOfTheWeek.indexOf(a) - daysOfTheWeek.indexOf(b));
    const [startTime, endTime] = timesString.split(' - ');

    return {
      days,
      startTime,
      endTime,
    };
  });
};

export const getFormattedRecurringStatus = (recurringStatus) => {
  const statusObjects = parseRecurringStatus(recurringStatus);

  return statusObjects.map((status) => {
    const { days, startTime, endTime } = status;

    const startDate = DateTime.fromFormat(startTime, 'h:mm a');

    const endDate = DateTime.fromFormat(endTime, 'h:mm a');

    const timeString =
      startDate >= endDate
        ? `12:00 AM - ${endTime}; ${startTime} - 11:59 PM`
        : `${startTime} - ${endTime}`;

    return `${days.join(', ')} - ${timeString}`;
  });
};

export const getQueryParameter = (param) => {
  const { search } = window.location;
  const params = new URLSearchParams(search);

  return params.get(param);
};
